import { HttpParams } from '@angular/common/http'
import { NgClass, NgIf, NgStyle } from '@angular/common'
import { Component, DestroyRef, OnInit, inject } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormsModule } from '@angular/forms'
import { FaIconComponent } from '@fortawesome/angular-fontawesome'
import { faX } from '@fortawesome/free-solid-svg-icons'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import imageCompression, { Options } from 'browser-image-compression'

import { ListItem, PunchListItem, RepositoryModal } from '../../shared'
import type { PunchListEditorParent } from './punch-list.types'

@Component({
    templateUrl: './punch-list-item.component.html',
    standalone: true,
    imports: [FormsModule, WjInputModule, NgIf, NgClass, NgStyle, FaIconComponent],
})
export class PunchListItemComponent extends RepositoryModal<PunchListItem> implements OnInit {
    private destroyRef = inject(DestroyRef)

    faX = faX

    /** Parent list screen (for firms, job label, users). */
    parentPage!: PunchListEditorParent

    firmOptions: ListItem<number | null>[] = []
    contactOptions: ListItem<number | null>[] = []
    vanConUserOptions: ListItem<string | null>[] = []
    boolMap: ListItem<boolean>[] = [new ListItem(true, 'Yes'), new ListItem(false, 'No')]

    images: any[] = []
    videos: any[] = []
    documents: any[] = []
    progress = 0

    /** Trade firm contact row visible only when a non-sentinel firm is selected. */
    get showTradeContact(): boolean {
        return this.target.responsibleFirmId != null
    }

    get vanConResponsibleDisplay(): string {
        const id = this.target.vanConResponsibleUserId
        if (!id) return ''
        return this.vanConUserOptions.find(o => o.value === id)?.display ?? id
    }

    get firmResponsibleDisplay(): string {
        const id = this.target.responsibleFirmId
        return this.firmOptions.find(o => o.value === id)?.display ?? ''
    }

    get contactDisplay(): string {
        const id = this.target.contactId
        if (id == null) return '(none)'
        return this.contactOptions.find(o => o.value === id)?.display ?? String(id)
    }

    ngOnInit() {
        this.parentPage = this.editOptions.initializeParams as PunchListEditorParent
        this.normalizeLegacyVanConFirmSelection()
        this.buildFirmOptions()
        this.buildVanConUserOptions()
        if (this.isAdding) {
            this.target.job = this.parentPage.selectedJob()
            this.target.done = false
            this.target.location ??= ''
            this.target.scope ??= ''
            this.target.projectMateNumber ??= ''
            this.target.description ??= ''
            this.target.notes ??= ''
        }
        if (this.isDuplicating) {
            this.target.imageUrls = []
            this.target.imageFileNames = []
        }
        this.target.imageUrls ??= []
        this.target.imageFileNames ??= []
        this.buildContactOptions()
    }

    /** Map Vista rows that pointed at the VanCon vendor firm to the sentinel (null) firm selection. */
    private normalizeLegacyVanConFirmSelection() {
        const vanConId = PunchListItemComponent.vanConFirmIdFromRows(this.parentPage.firms)
        if (vanConId != null && this.target.responsibleFirmId === vanConId) {
            this.target.responsibleFirmId = null
            this.target.contactId = null
        }
    }

    private static vanConFirmIdFromRows(firms: PunchListEditorParent['firms']): number | undefined {
        return firms.find(f => f.name?.trim().toLowerCase() === 'vancon')?.id
    }

    private buildFirmOptions() {
        const nonVanCon = this.parentPage.firms.filter(f => !PunchListItemComponent.isVanConFirmName(f.name))
        this.firmOptions = [
            new ListItem<number | null>(null, 'Only VanCon Responsible'),
            ...nonVanCon.map(f => new ListItem<number | null>(f.id, f.name)),
        ]
    }

    private static isVanConFirmName(name: string | undefined): boolean {
        return name?.trim().toLowerCase() === 'vancon'
    }

    private buildVanConUserOptions() {
        const active = this.parentPage.activeUsers ?? []
        const sorted = [...active].sort((a, b) => a.displayName.localeCompare(b.displayName))
        const options: ListItem<string | null>[] = [
            new ListItem<string | null>(null, '(Select)'),
            ...sorted.map(u => new ListItem<string | null>(u.id, u.displayName)),
        ]
        const cur = this.target.vanConResponsibleUserId
        if (cur && !sorted.some(u => u.id === cur)) {
            const u = this.parentPage.allUsers?.find(x => x.id === cur)
            if (u) {
                options.splice(1, 0, new ListItem<string | null>(u.id, `${u.displayName} (inactive)`))
            }
        }
        this.vanConUserOptions = options
    }

    buildContactOptions() {
        const firmId = this.target.responsibleFirmId
        if (firmId == null) {
            this.contactOptions = [new ListItem<number | null>(null, '(none)')]
            this.target.contactId = null
            return
        }
        const firm = this.parentPage.firms.find((x: { id: number }) => x.id === firmId)
        const contacts = firm?.contacts as Record<string, unknown>[] | undefined
        this.contactOptions = [new ListItem<number | null>(null, '(none)')]
        if (contacts?.length) {
            const sorted = contacts
                .map(c => new ListItem<number | null>(c.contactCode as number, PunchListItemComponent.contactLabel(c)))
                .sort((a, b) => a.display.localeCompare(b.display))
            this.contactOptions.push(...sorted)
        }
        const cur = this.target.contactId
        if (cur != null && !this.contactOptions.some(o => o.value === cur)) {
            this.target.contactId = null
        }
    }

    private static contactLabel(c: Record<string, unknown>): string {
        const first = c.firstName
        const last = c.lastName
        const parts = [first, last].filter(x => typeof x === 'string' && (x as string).length > 0) as string[]
        if (parts.length > 0) return parts.join(' ')
        const sortName = c.sortName
        if (typeof sortName === 'string' && sortName.length > 0) return sortName
        return String(c.contactCode ?? '')
    }

    onResponsibleChange() {
        this.buildContactOptions()
    }

    protected onBeforeSave(): boolean {
        if (!this.parentPage.canEdit) {
            return true
        }
        if (!this.target.job?.trim()) {
            this.services.notification.warning('Job is required')
            return false
        }
        if (!this.target.vanConResponsibleUserId?.trim()) {
            this.services.notification.warning('VanCon responsible is required')
            return false
        }
        return true
    }

    private static readonly VIDEO_EXTENSIONS = ['.mp4', '.webm', '.mov', '.avi']

    isVideo(url: string): boolean {
        const lower = url.toLowerCase()
        return PunchListItemComponent.VIDEO_EXTENSIONS.some(ext => lower.endsWith(ext))
    }

    isPdf(url: string): boolean {
        return url.toLowerCase().endsWith('.pdf')
    }

    getExistingFileSrc(url: string) {
        return './Resources/Images/PunchListItem/' + url
    }

    getExistingImageSrc(url: string) {
        if (url.indexOf('.pdf') > -1) {
            return './Resources/pdfImage.png'
        }
        return './Resources/Images/PunchListItem/' + url + '?w=300'
    }

    getPendingImageSrc(image: any) {
        if (image.type === 'application/pdf') {
            return './Resources/pdfImage.png'
        }
        return image.url
    }

    openImage(url: string) {
        const docId = PunchListItemComponent.documentIdFromStoredUrl(url)
        if (docId) {
            window.open(`api/PunchList/DownloadDocument?documentId=${encodeURIComponent(docId)}`, '_blank')
            return
        }
        window.open('./Resources/Images/PunchListItem/' + url, '_blank')
    }

    private static documentIdFromStoredUrl(url: string): string | null {
        const parts = url.split('/').filter(Boolean)
        if (parts.length < 2) return null
        const file = parts[1]
        const dot = file.lastIndexOf('.')
        const idPart = dot > 0 ? file.slice(0, dot) : file
        return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(idPart) ? idPart : null
    }

    displayFileName(index: number): string {
        const names = this.target.imageFileNames
        const fromApi = names?.[index]
        if (fromApi) return fromApi
        const url = this.target.imageUrls[index]
        return url?.split('/').pop() ?? ''
    }

    async onPhotoChanged(event: Event) {
        const input = event.target as HTMLInputElement
        if (!input.files) return
        const files = input.files
        const progress = (p: number) => {
            this.progress = p
        }

        for (let i = 0; i < files.length; i++) {
            const file = files[i]
            const options: Options = {
                maxSizeMB: 2,
                maxWidthOrHeight: 1920,
                useWebWorker: true,
                onProgress: progress,
            }
            const compressedFile = await imageCompression(file, options)
            ;(compressedFile as any)['url'] = await imageCompression.getDataUrlFromFile(compressedFile)
            this.images.push(compressedFile)
        }
        input.value = ''
    }

    onVideoChanged(event: Event) {
        const input = event.target as HTMLInputElement
        if (!input.files) return
        for (let i = 0; i < input.files.length; i++) {
            this.videos.push(input.files[i])
        }
        input.value = ''
    }

    onDocumentChanged(event: Event) {
        const input = event.target as HTMLInputElement
        if (!input.files) return
        for (let i = 0; i < input.files.length; i++) {
            this.documents.push(input.files[i])
        }
        input.value = ''
    }

    removeImage(image: any) {
        const idx = this.images.indexOf(image)
        if (idx > -1) this.images.splice(idx, 1)
    }

    removeVideo(video: any) {
        const idx = this.videos.indexOf(video)
        if (idx > -1) this.videos.splice(idx, 1)
    }

    removeDocument(doc: any) {
        const idx = this.documents.indexOf(doc)
        if (idx > -1) this.documents.splice(idx, 1)
    }

    private get allFiles(): any[] {
        return [...this.images, ...this.videos, ...this.documents]
    }

    protected override onSave(resolveSave: (result?: any) => void, rejectSave: (reason?: any) => void) {
        if (!this.parentPage.canEdit) {
            const params = new HttpParams()
                .set('id', this.target.id)
                .set('done', this.target.done === true)
            this.services.data
                .put('api/PunchList/SetDone', null, params)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: () => resolveSave(this.target),
                    error: errorResult => rejectSave(errorResult),
                })
            return
        }

        const observable = this.isAdding || this.isDuplicating ? this.repositoryAdd() : this.repositoryEdit()
        observable.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: successResult => {
                if (this.allFiles.length === 0) {
                    resolveSave(successResult)
                    return
                }

                const uploadData = new FormData()
                uploadData.append('id', successResult.id)
                const files = this.allFiles
                for (let i = 0; i < files.length; i++) {
                    uploadData.append('myFile' + i, files[i], files[i].name)
                }
                this.services.data
                    .post('api/PunchList/UploadImages', uploadData)
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe({
                        next: () => resolveSave(successResult),
                        error: errorResult => rejectSave(errorResult),
                    })
            },
            error: errorResult => rejectSave(errorResult),
        })
    }
}
